/**
 * K5 — gyártási emlékeztető, and K7 — előrejelzés és konyhai naptár. Handover
 * rev7 §11.6, as A4 print views.
 *
 * These two are about a day rather than about an order, which is why they are
 * not `KitchenDoc.tsx`: there is no order reference, no delivery block and no
 * version to stamp ÉRVÉNYTELEN on. What they share with it is everything that
 * matters about a kitchen document —
 *
 *  - **From the reading the email uses.** `public.kitchen_day_doc` builds it
 *    once and `outbox_digest_json` hands the same object to the template, so
 *    the HTML email and this sheet cannot carry different figures (§11.6 asks
 *    for both and never says they may disagree).
 *  - **No prices.** There are none in the reading.
 *  - **Allergens as code and full name**, with §7.3's verification state
 *    beside them. Example data never reads as verified.
 *  - **A gap prints as a gap.** §7.3's interim catalogue has no station for
 *    any dish, so K5's one group is the gap, named.
 *
 * A4, Raleway 10 pt and page numbers come from `print/styles.ts` and
 * `print/paginate.ts`, as they do for the order documents.
 */

import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { formatter } from '../../page/format.ts'
import { ALLERGENS_BEING_VERIFIED } from '../../page/model.ts'
import { consoleCall, ConsoleError } from '../api.ts'
import { useConsole } from '../context.ts'
import { paginate, pageLabel } from './paginate.ts'
import { PRINT_STYLES } from './styles.ts'
import { SiteNotice } from '../../routes/SiteNotice.tsx'

/* Rows that fit on an A4 sheet at Raleway 10 pt inside 14 mm margins. K5's
   rows are a dish line plus one line per order under it, so a station is
   counted as its rows; K7's calendar page is fifteen rows and its forecast is
   one row a dish. Conservative, for the reason KitchenDoc.tsx is. */
const K5_ROWS_FIRST = 20
const K5_ROWS_REST = 30
const K7_DISHES_FIRST = 18
const K7_DISHES_REST = 32

export interface DayAllergen {
  code: number | string
  name_hu?: string | null
}

export interface ProductionOrderRow {
  ref: string
  event_date: string
  event_start: string | null
  guests: number
  portions: number
  production_units: number | null
  is_gf_portion: boolean
  unit: string | null
}

export interface ProductionDish {
  dish_id: string
  name_hu: string | null
  unit: string | null
  production_unit: string | null
  portion_definition: string | null
  is_drink: boolean
  allergens: DayAllergen[] | null
  allergen_status: string | null
  allergen_test_marked: boolean | null
  portions: number
  gf_portions: number
  production_units: number | null
  orders: ProductionOrderRow[]
}

export interface ProductionStation {
  station: string | null
  portions: number
  gf_portions: number
  dishes: ProductionDish[]
}

export interface ProductionDocument {
  kind: 'K5'
  for_date: string
  generated_at: string
  bands: string[]
  stations: ProductionStation[]
  orders: {
    order_id: string
    ref: string
    event_date: string
    event_start: string | null
    guests: number
    gf_guests: number
    fulfilment: string
    slot_label: string | null
    status: string
    dietary_note: string | null
    labels_blocked: boolean
    bands: string[]
  }[]
  totals: {
    portions: number
    gf_portions: number
    drinks: number
    dishes: number
    orders: number
    stations: number
  }
}

export interface ForecastDay {
  date: string
  holiday_name: string | null
  kitchen_closed: boolean
  is_clash_date: boolean
  note: string | null
  portion_cap: number | null
  max_orders_per_day: number | null
  max_covers_per_day: number | null
  soft_threshold: number | null
  committed_portions: number
  committed_orders: number
  committed_covers: number
  distinct_savoury: number
  distinct_sweet: number
  overridden_orders: number
}

export interface ForecastDish {
  dish_id: string
  name_hu: string | null
  station: string | null
  unit: string | null
  vegleges: number
  visszaigazolt: number
  fuggo: number
  orders: number
  previous: { vegleges: number; visszaigazolt: number; fuggo: number } | null
  changed: boolean
}

export interface ForecastDocument {
  kind: 'K7'
  for_date: string
  until: string
  forecast_bd: number
  generated_at: string
  previous_for_date: string | null
  bands: string[]
  days: ForecastDay[]
  dishes: ForecastDish[]
  gone: ForecastDish[]
  totals: {
    dishes: number
    days: number
    vegleges: number
    visszaigazolt: number
    fuggo: number
    orders: number
    covers: number
  }
}

export type DayDocument = ProductionDocument | ForecastDocument

const fmt = formatter('hu')
const MISSING = '— nincs adat'

/* §5.12: the unit never wraps away from its number. The same join `qty()` in
   `packages/emails/src/format.ts` makes for the email render of these rows. */
const NBSP = '\u00a0'
const qty = (value: number, unit: string | null | undefined): string =>
  `${fmt.n(value)}${NBSP}${unit ?? 'db'}`

const asDate = (value: string | null): string =>
  value === null ? MISSING : fmt.date(new Date(`${value}T00:00:00`), true)

/**
 * §11.6: allergens as code and full name, with §7.3's verification state
 * beside them.
 *
 * An empty list is a claim — this dish contains none of the fourteen — and on
 * unverified data that claim is not made at all. One wording, shared with
 * `KitchenDoc.tsx`, `packages/emails/src/kitchenDoc.ts` and `osszesites.ts`,
 * so no two renders of one dish say different things about its allergens.
 */
export function allergenText(dish: {
  allergens: DayAllergen[] | null
  allergen_status: string | null
  allergen_test_marked: boolean | null
}): string {
  const parts = (dish.allergens ?? []).map((a) => `${a.code} ${a.name_hu ?? ''}`.trim())
  const verified = dish.allergen_status === 'verified' && dish.allergen_test_marked !== true
  // Only a verified dish prints a plain list, and only a verified dish with
  // an empty list prints *nincs*. Anything else opens with *Allergének
  // ellenőrzés alatt* (Barna, 10 October 2026). A kitchen document keeps
  // the unverified list after it, marked as such, because the kitchen
  // judges dietary requests by it; the customer sees the phrase alone.
  if (!verified) {
    return parts.length === 0
      ? ALLERGENS_BEING_VERIFIED.hu
      : `${ALLERGENS_BEING_VERIFIED.hu} — nem ellenőrzött lista: ${parts.join(' · ')}`
  }
  return parts.length === 0 ? 'allergének: nincs' : `allergének: ${parts.join(' · ')}`
}

/** §7.3: anything other than Robi's own sign-off prints as example data. */
export function allergenCaveat(
  dishes: { allergen_status: string | null; allergen_test_marked: boolean | null }[],
): string | null {
  const unverified = dishes.some(
    (dish) => dish.allergen_status !== 'verified' || dish.allergen_test_marked === true,
  )
  return unverified
    ? 'Az allergénadatok ELLENŐRZÉSRE VÁRNAK. Példaadatok, nem a konyha aláírt listája (§7.3).'
    : null
}

/** A committed figure against the limit in force for the day (§8.1). A limit
 *  the register has no value for prints as a gap, never as a zero. */
export function against(committed: number, limit: number | null): string {
  return limit === null ? `${fmt.n(committed)} / ${MISSING}` : `${fmt.n(committed)} / ${fmt.n(limit)}`
}

/** What the calendar row says about a day, in the order a kitchen reads it. */
export function dayNotes(day: ForecastDay): string[] {
  const notes: string[] = []
  if (day.kitchen_closed) notes.push('KONYHA ZÁRVA')
  if (day.is_clash_date) notes.push('ÜTKÖZŐ NAP')
  if (day.holiday_name !== null && day.holiday_name !== '') notes.push(day.holiday_name)
  if (day.overridden_orders > 0) notes.push('FELÜLBÍRÁLAT')
  if (day.soft_threshold !== null && day.committed_portions > day.soft_threshold) {
    notes.push('kapacitásküszöb felett')
  }
  if (day.note !== null && day.note !== '') notes.push(day.note)
  return notes
}

export function changeNote(dish: ForecastDish, previousDate: string | null): string {
  if (previousDate === null) return '—'
  if (!dish.changed) return 'változatlan'
  if (dish.previous === null) return 'új a legutóbbi előrejelzés óta'
  const delta =
    dish.vegleges + dish.visszaigazolt + dish.fuggo -
    (dish.previous.vegleges + dish.previous.visszaigazolt + dish.previous.fuggo)
  if (delta === 0) return 'átsorolódott az oszlopok között'
  // The qualifier stays on every row of the column, so one column does not
  // mix qualified and unqualified statements.
  return `${delta > 0 ? '+' : ''}${fmt.n(delta)} adag a legutóbbi előrejelzés óta`
}

/**
 * K5's rows, flattened for pagination: a dish line, then one line per order
 * under it. They have to break together or not at all as far as a sheet can
 * manage, which is why the dish line carries its own orders' count.
 */
export interface K5Row {
  station: string | null
  stationFirst: boolean
  dish: ProductionDish
  order: ProductionOrderRow | null
}

export function k5Rows(doc: ProductionDocument): K5Row[] {
  const rows: K5Row[] = []
  for (const station of doc.stations) {
    let first = true
    for (const dish of station.dishes) {
      rows.push({ station: station.station, stationFirst: first, dish, order: null })
      first = false
      for (const order of dish.orders) {
        rows.push({ station: station.station, stationFirst: false, dish, order })
      }
    }
  }
  return rows
}

function Bands({ bands }: { bands: string[] }) {
  return (
    <>
      {bands.map((band) => (
        <div className="band" key={band} data-testid={`band-${band}`}>{band}</div>
      ))}
    </>
  )
}

function Foot({ doc, page, pages }: { doc: DayDocument; page: number; pages: number }) {
  return (
    <div className="foot">
      <span>{doc.kind} · {asDate(doc.for_date)}</span>
      <span>Planteen · {fmt.date(new Date(doc.generated_at), false)}</span>
      <span className="page" data-testid={`page-${page}`}>{pageLabel(page, pages)}</span>
    </div>
  )
}

function K5Rows({ rows }: { rows: K5Row[] }) {
  return (
    <table>
      <thead>
        <tr>
          <th>Fogás és rendelés</th>
          <th className="num">Adag</th>
          <th className="num">Gyártási egység</th>
          <th>Allergének</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) =>
          row.order === null ? (
            <tr key={`d-${row.dish.dish_id}-${index}`} data-testid={`k5-dish-${row.dish.dish_id}`}>
              <td className="gf">
                {row.stationFirst && (
                  <div className="station">
                    {row.station === null ? `Állomás ${MISSING}` : row.station}
                  </div>
                )}
                {row.dish.name_hu ?? row.dish.dish_id}
              </td>
              <td className="num gf">{qty(row.dish.portions, row.dish.unit)}</td>
              <td className={`num${row.dish.production_units === null ? ' gap' : ''}`}>
                {row.dish.production_units === null
                  ? MISSING
                  : `${fmt.n(row.dish.production_units)} ${row.dish.production_unit ?? ''}`.trim()}
              </td>
              <td className="allergens">{allergenText(row.dish)}</td>
            </tr>
          ) : (
            <tr key={`o-${row.dish.dish_id}-${row.order.ref}-${index}`} data-testid={`k5-order-${row.order.ref}`}>
              <td className="sub-row">
                {row.order.ref} · {asDate(row.order.event_date)}
                {row.order.is_gf_portion ? ' · GLUTÉNMENTES, külön doboz' : ''}
              </td>
              <td className="num">{qty(row.order.portions, row.order.unit)}</td>
              <td className="num" />
              <td />
            </tr>
          ),
        )}
      </tbody>
    </table>
  )
}

function CalendarTable({ days }: { days: ForecastDay[] }) {
  return (
    <table data-testid="k7-calendar">
      <thead>
        <tr>
          <th>Nap</th>
          <th className="num">Rendelés</th>
          <th className="num">Vendég</th>
          <th className="num">Adag</th>
          <th className="num">Sós</th>
          <th className="num">Édes</th>
          <th>Megjegyzés</th>
        </tr>
      </thead>
      <tbody>
        {days.map((day) => (
          <tr key={day.date} data-testid={`k7-day-${day.date}`}>
            <td>{asDate(day.date)}</td>
            <td className="num">{against(day.committed_orders, day.max_orders_per_day)}</td>
            <td className="num">{against(day.committed_covers, day.max_covers_per_day)}</td>
            <td className="num">{against(day.committed_portions, day.portion_cap)}</td>
            <td className="num">{fmt.n(day.distinct_savoury)}</td>
            <td className="num">{fmt.n(day.distinct_sweet)}</td>
            <td className="allergens">{dayNotes(day).join(' · ') || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function ForecastRows({ rows, previous }: { rows: ForecastDish[]; previous: string | null }) {
  return (
    <table data-testid="k7-forecast">
      <thead>
        <tr>
          <th>Fogás</th>
          <th className="num">Végleges</th>
          <th className="num">Visszaigazolt</th>
          <th className="num">Függő</th>
          <th>Változás</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((dish) => (
          <tr key={dish.dish_id} data-testid={`k7-dish-${dish.dish_id}`}>
            <td>{dish.name_hu ?? dish.dish_id}</td>
            <td className="num">{fmt.n(dish.vegleges)}</td>
            <td className="num">{fmt.n(dish.visszaigazolt)}</td>
            <td className="num">{fmt.n(dish.fuggo)}</td>
            <td className={dish.changed ? 'gf' : undefined}>{changeNote(dish, previous)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function KitchenDayDocView(): JSX.Element {
  const { kind = 'K5' } = useParams()
  const [search] = useSearchParams()
  const { config, session } = useConsole()
  const [doc, setDoc] = useState<DayDocument | null>(null)
  const [error, setError] = useState<string | null>(null)

  const day = search.get('nap')

  useEffect(() => {
    let live = true
    consoleCall<DayDocument>(
      'kitchen_day_doc',
      { kind, ...(day === null ? {} : { date: day }) },
      { config, session },
    )
      .then((result) => live && setDoc(result))
      .catch((cause: unknown) =>
        live && setError(cause instanceof ConsoleError ? cause.message : 'Nem sikerült betölteni.')
      )
    return () => {
      live = false
    }
  }, [config, session, kind, day])

  const paged = useMemo(() => {
    if (doc === null) return null
    if (doc.kind === 'K5') return paginate(k5Rows(doc), K5_ROWS_FIRST, K5_ROWS_REST)
    // K7's calendar page is a sheet of its own, by §11.6: "a calendar page at
    // the top: one A4 table". The forecast paginates under it.
    return paginate(doc.dishes, K7_DISHES_FIRST, K7_DISHES_REST)
  }, [doc])

  if (error !== null) {
    return (
      <div className="plt-print" data-testid="kitchen-day-doc-error">
        <style>{PRINT_STYLES}</style>
        <div className="sheet"><p>{error}</p></div>
      </div>
    )
  }
  if (doc === null || paged === null) {
    return (
      <div className="plt-print">
        <style>{PRINT_STYLES}</style>
        <div className="sheet"><p>Betöltés…</p></div>
      </div>
    )
  }

  // K7 puts the calendar on its own first sheet; K5 starts with its rows.
  const pages = doc.kind === 'K7' ? paged.pages + 1 : paged.pages
  const caveat =
    doc.kind === 'K5'
      ? allergenCaveat(doc.stations.flatMap((station) => station.dishes))
      : null

  return (
    <div className="plt-print" data-testid={`kitchen-day-doc-${doc.kind}`}>
      <style>{PRINT_STYLES}</style>
      <SiteNotice />

      <div className="toolbar">
        <button type="button" onClick={() => globalThis.print?.()} data-testid="print">
          Nyomtatás
        </button>
        <span>A4 · Raleway 10 pt · {pages} oldal</span>
        <span className="spacer">
          {doc.kind} · {asDate(doc.for_date)}
        </span>
      </div>

      {doc.kind === 'K7' && (
        <section className="sheet" data-testid="sheet-1">
          {/* The document's own bands, not a second derivation of them: the
              HTML email reads the same array (§11.6, one reading). */}
          <Bands bands={doc.bands ?? []} />
          <h1>Előrejelzés és konyhai naptár</h1>
          <p className="sub">
            {asDate(doc.for_date)} – {asDate(doc.until)} · {fmt.n(doc.forecast_bd)} munkanap
          </p>
          <CalendarTable days={doc.days} />
          <p className="sub" data-testid="b43-note">
            A Sós és az Édes oszlop a napra eső rendelések különböző fogásainak számát
            mutatja. Tájékoztató adat: napi fogásszámra nincs érvényben korlát (B43).
          </p>
          <div className="totals" data-testid="k7-calendar-totals">
            {fmt.n(doc.totals.orders)} rendelés · {fmt.n(doc.totals.covers)} fő
            {' · '}
            {doc.previous_for_date === null
              ? 'előző előrejelzés nincs — ez az első, így nincs mihez hasonlítani'
              : `előző előrejelzés: ${asDate(doc.previous_for_date)}`}
          </div>
          <Foot doc={doc} page={1} pages={pages} />
        </section>
      )}

      {paged.sheets.map((sheet) => {
        const page = doc.kind === 'K7' ? sheet.page + 1 : sheet.page
        const last = sheet.page === paged.pages
        return (
          <section className="sheet" key={`rows-${sheet.page}`} data-testid={`sheet-${page}`}>
            {sheet.page === 1 && doc.kind === 'K5' && (
              <>
                <Bands bands={doc.bands} />
                <h1>Gyártási emlékeztető</h1>
                <p className="sub">
                  Gyártási nap: {asDate(doc.for_date)} · {fmt.n(doc.totals.orders)} rendelés
                </p>
              </>
            )}
            {sheet.page === 1 && doc.kind === 'K7' && <h1>Fogásonkénti előrejelzés</h1>}

            {doc.kind === 'K5'
              ? <K5Rows rows={sheet.rows as K5Row[]} />
              : <ForecastRows rows={sheet.rows as ForecastDish[]} previous={doc.previous_for_date} />}

            {doc.kind === 'K5' && caveat !== null && (
              <div className="block" data-testid={`allergen-caveat-${sheet.page}`}>
                <p>{caveat}</p>
              </div>
            )}

            {last && doc.kind === 'K5' && (
              <>
                <div className="totals" data-testid="totals">
                  Összesen {fmt.n(doc.totals.portions)} adag · {fmt.n(doc.totals.dishes)} fogás
                  {' · '}{fmt.n(doc.totals.orders)} rendelés
                  {doc.totals.gf_portions > 0
                    ? ` · ebből ${fmt.n(doc.totals.gf_portions)} gluténmentes, külön dobozban`
                    : ''}
                </div>

                {doc.stations.length === 0 && (
                  <p className="gap" data-testid="k5-empty">
                    Ezen a gyártási napon nincs gyártandó tétel.
                  </p>
                )}

                <div className="block" data-testid="k5-orders">
                  <h2>A nap rendelései</h2>
                  <table>
                    <thead>
                      <tr>
                        <th>Rendelés</th>
                        <th>Rendezvény</th>
                        <th className="num">Vendégek</th>
                        <th>Jelzés</th>
                      </tr>
                    </thead>
                    <tbody>
                      {doc.orders.map((order) => (
                        <tr key={order.ref} data-testid={`k5-order-row-${order.ref}`}>
                          <td>{order.ref}</td>
                          <td>
                            {asDate(order.event_date)}
                            {order.event_start === null ? '' : ` · ${order.event_start.slice(0, 5)}`}
                          </td>
                          <td className="num">{qty(order.guests, 'fő')}</td>
                          <td className="allergens">
                            {order.bands.length === 0 ? '—' : order.bands.join(' · ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {last && doc.kind === 'K7' && (
              <>
                {doc.dishes.length === 0 && (
                  <p className="gap" data-testid="k7-empty">
                    Ebben az időszakban nincs rendelés.
                  </p>
                )}

                {doc.gone.length > 0 && (
                  <div className="block" data-testid="k7-gone">
                    <h2>Lekerült az előrejelzésről</h2>
                    <ul>
                      {doc.gone.map((dish) => (
                        <li key={dish.dish_id}>
                          {dish.name_hu ?? dish.dish_id} — a legutóbbi előrejelzésen még
                          szerepelt, most már nem szerepel egyetlen rendelésen sem
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="totals" data-testid="totals">
                  Végleges {fmt.n(doc.totals.vegleges)} · visszaigazolt{' '}
                  {fmt.n(doc.totals.visszaigazolt)} · függő {fmt.n(doc.totals.fuggo)} adag
                </div>
              </>
            )}

            <Foot doc={doc} page={page} pages={pages} />
          </section>
        )
      })}
    </div>
  )
}
